import type { IOrder } from "@commonTypes/order"
import { baseServiceRequest } from "../baseServiceRequest"
import type {
  IAccount,
  IAgedAccount,
  IUploadNotesRequest,
  IUpdateAccountStatusRequest,
  IUpdateAccountNameRequest,
  IEnergyImpactItem,
  ILinkedAccount,
  ICorrespondence,
  IAccountEvent,
  IAccountNotification,
  ICorrespondenceLetter,
  IWelcomeKit,
  IAccountNumberHistory,
  IAccountOrderAuthorization,
  IAccountTPV,
  IAccountPayableBill,
  IAccountContractHistory,
  IBillImage,
  IAccountServiceHistory,
  IVoidAndReissueCheckRequest,
  ISaveAddressRequestBody,
  ISaveAddressResponse,
  IDelinkAccountRequest,
  TDelinkAccountResponse,
  IAccountInboundDropRequest,
  IUpdateAccountEmailRequest,
  IActivateAccountRequest,
  IAccountPrimaryPhoneNumberRequest,
  IAccountOfferChangeDetails,
  IDropOfferRequest,
  IAccountReject,
  IRejectAccountResponse,
  IDropReason,
  IRequestAccountDropRequest,
  IAccountExistsParams,
  IRequestAccountDropResponse,
  IAccountExistsResponse,
  IAccountReferralData,
} from "./types"

import type {
  IAccountPromo,
  IAddPromoForm,
  IAccountRewards,
} from "@apis/rewards/types"

export const getAccount = async (account_id: string): Promise<IAccount> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}`,
  })
  return response.data
}

export const uploadNotes = async (request: FormData | IUploadNotesRequest) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: "/accounts/notes",
    data: request,
    skipAuditInterceptor: true,
  })
  return response.data
}

export const updateAccountStatus = async ({
  account_id,
  request,
}: {
  account_id: string
  request: IUpdateAccountStatusRequest
}) => {
  const response = await baseServiceRequest({
    method: "PUT",
    url: `/accounts/${account_id}`,
    data: request,
  })
  return response.data
}

export const updateAccountName = async ({
  account_id,
  request,
}: {
  account_id: string
  request: IUpdateAccountNameRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/${account_id}/membership_profile/info`,
    data: request,
  })
  return response.data
}

export const updateAccountEmail = async ({
  account_id,
  request,
}: {
  account_id: string
  request: IUpdateAccountEmailRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/${account_id}/membership_profile/email`,
    data: request,
  })
  return response.data
}

export const getAgedAccounts = async (): Promise<IAgedAccount[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: "/internal/aged_accounts",
  })
  return response.data
}

export const getEnergyImpact = async (
  account_id: string,
): Promise<IEnergyImpactItem[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/energy_impact`,
  })
  return response.data
}

export const getLinkedAccounts = async (
  account_id: string,
): Promise<ILinkedAccount[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/linked_accounts`,
  })
  return response.data
}

export const getAccountCorrespondences = async (
  account_id: string,
): Promise<ICorrespondence[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/correspondence_letters`,
  })
  return response.data.data
}

export const getAccountEvents = async (
  prospect_guid: string,
  account_id: string,
): Promise<IAccountEvent[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `prospects/prospect/${prospect_guid}/account/${account_id}/timeline?load_all=y`,
  })
  return response.data
}

export const getAccountNotifications = async (
  prospect_guid: string,
  account_id: string,
): Promise<IAccountNotification[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `prospects/prospect/${prospect_guid}/account/${account_id}/notifications`,
  })
  return response.data
}

export const getAccountCorrespondenceLetter = async ({
  account_id,
  account_correspondence_id,
}: {
  account_id: string
  account_correspondence_id: number
}): Promise<ICorrespondenceLetter> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `accounts/${account_id}/correspondence_letters/${account_correspondence_id}`,
  })
  return response.data
}

export const getWelcomeKit = async (
  account_id: string,
): Promise<IWelcomeKit> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/welcome_kit`,
  })
  return response.data
}

export const getAccountNumberHistory = async (
  account_id: string,
): Promise<IAccountNumberHistory[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/account_num_histories`,
  })
  return response.data
}

export const updateAccountNumberHistory = async ({
  account_id,
  request,
}: {
  account_id: string
  request: {
    selected_history: IAccountNumberHistory
    agent_id: string
  }
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/${account_id}/account_num_histories`,
    data: request,
  })
  return response.data
}

export const setAccountPrimaryPhoneNumber = async ({
  account_id,
  request,
}: {
  account_id: string
  request: IAccountPrimaryPhoneNumberRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/${account_id}/membership_profile/phone`,
    data: request,
  })
  return response.data
}

export const getRewardsByAccount = async (
  account_id: string,
): Promise<IAccountRewards> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `accounts/${account_id}/rewards/`,
  })
  return response.data
}

export const getAccountOrdersList = async (
  account_id: string,
): Promise<IOrder[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/orders`,
  })
  return response.data
}

export const getAccountPromos = async (
  account_id: string,
): Promise<IAccountPromo[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `accounts/${account_id}/account_promos/`,
  })
  return response.data
}

export const getAccountOrderAuthorizations = async (
  account_id: string,
): Promise<IAccountOrderAuthorization[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/order_authorizations`,
  })
  return response.data
}

export const addPromo = async ({
  account_id,
  data,
}: {
  account_id: string
  data: IAddPromoForm
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `accounts/${account_id}/account_promos`,
    data,
  })
  return response.data
}

export const getAccountTPVS = async (
  account_id: string,
): Promise<IAccountTPV[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/tpvs`,
  })
  return response.data
}

export const getAccountPayableBills = async (
  account_id: string,
): Promise<IAccountPayableBill[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/account_payable_bills`,
  })
  return response.data
}

export const voidAndReissueCheck = async ({
  account_id,
  account_payable_bill_id,
  data,
}: {
  account_id: string
  account_payable_bill_id: number
  data: IVoidAndReissueCheckRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `accounts/${account_id}/account_payable_bills/${account_payable_bill_id}/void_and_reissue`,
    data,
  })
  return response.data
}

export const getAccountContractHistories = async (
  account_id: string,
): Promise<IAccountContractHistory[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/contract_histories`,
  })
  return response.data
}

export const getAccountBillImages = async (
  account_id: string,
): Promise<IBillImage[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/bill_images`,
  })
  return response.data
}

export const getAccountServiceHistories = async (
  account_id: string,
): Promise<IAccountServiceHistory[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/service_histories`,
  })
  return response.data
}

export const getOfferChangeDetails = async (
  account_id: string,
): Promise<IAccountOfferChangeDetails> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/offers/change/details`,
  })
  return response.data
}

export const saveAccountAddress = async ({
  account_id,
  address_type_code,
  data,
}: {
  account_id: string
  address_type_code: string
  data: ISaveAddressRequestBody
}): Promise<ISaveAddressResponse> => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/${account_id}/address/${address_type_code}`,
    data,
  })
  return response.data
}

export const delinkAccount = async ({
  account_id,
  request,
}: {
  account_id: string
  request: IDelinkAccountRequest
}): Promise<TDelinkAccountResponse> => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/${account_id}/membership_profiles`,
    data: request,
  })
  return response.data
}

export const inboundDropAccount = async ({
  account_id,
  request,
}: {
  account_id: string
  request: IAccountInboundDropRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/${account_id}/drops/inbound`,
    data: request,
  })
  return response.data
}

export const activateAccount = async ({
  account_id,
  request,
}: {
  account_id: string
  request: IActivateAccountRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/${account_id}/activation`,
    data: request,
  })
  return response.data
}

export const dropOffer = async ({
  account_id,
  offer_code,
  request,
}: {
  account_id: string
  offer_code: string
  request: IDropOfferRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/${account_id}/drop_offer/${offer_code}`,
    data: request,
  })
  return response.data
}

export const getRejectByAccountId = async (
  account_id: string,
): Promise<IAccountReject> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/rejects/account/${account_id}`,
  })
  return response.data
}

export const getRejectById = async (
  reject_id: string,
): Promise<IRejectAccountResponse> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/rejects/${reject_id}`,
  })
  return response.data
}

export const getDropReasons = async (): Promise<IDropReason[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/drop_reasons`,
  })
  return response.data
}

export const requestAccountDrop = async ({
  account_id,
  request,
}: {
  account_id: string
  request: IRequestAccountDropRequest
}): Promise<IRequestAccountDropResponse> => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/${account_id}/drops`,
    data: request,
  })
  return response.data
}

export const saveRejectInformation = async ({
  reject_id,
  request,
}: {
  reject_id: string
  request: IRejectAccountResponse & { reject_rep: string }
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/accounts/rejects/${reject_id}`,
    data: request,
  })
  return response.data
}

export const getAccountReferrals = async (
  account_id: string,
): Promise<IAccountReferralData> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/${account_id}/referrals`,
  })
  return response.data
}

export const addReferral = async ({
  referrer_guid,
  account_id,
}: {
  referrer_guid: string
  account_id: string
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `referrals/${referrer_guid}/referred/${account_id}`,
  })
  return response.data
}

export const getAccountExists = async (
  params: IAccountExistsParams,
): Promise<IAccountExistsResponse> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/accounts/account_exists/${params.account_number}/${params.market_code}`,
    params: {
      ignore_status_group: params.ignore_status_group,
    },
  })
  return response.data
}
